import { ReturnItem, StatusHistory } from 'services/api/refund';
import { DetailCard } from '../DetailCard';
import { useTranslate } from '@tolgee/react';
import { Checkbox } from 'antd';
import { ProductInfoImage } from '../ProductInfo/Image';
import { ProductInfoTitle } from '../ProductInfo/Title';
import { CopyText } from '../CopyText';
import { formatNumber } from 'utils/numberFormat';
import { useEffect, useMemo } from 'react';
import { useGetReasonLabel } from 'hooks/business/refundReason/useGetReasonLabel';
import StatusHistoryModal, { useStatusLabel } from 'views/Refund/components/StatusHistoryModal';
import useModal from 'hooks/useModal';
import useChatHistory from 'views/Refund/components/useChatHIstory';
import dayjs from 'lib/dayjs';
import CountDown from 'views/Refund/components/CountDown';
import { css } from '@emotion/css';
import { useLazadaOrderReturnDetailContext } from './context';
import { UseLazadaReturnOpsReturn } from 'hooks/business/chat/useLazadaReturnOps';

interface LazadaOrderReturnItemsProps {
  returnSn: string;
  platform: General.PlatformUpperCase;
  unit: string;
  returnItems: ReturnItem[];
  lazadaReturnOps: UseLazadaReturnOpsReturn;
}

interface LazadaOrderReturnItemProps {
  returnSn: string;
  returnItem: ReturnItem;
  unit: string;
  multiple?: boolean;
}

const DueTime = ({ dueDateMs }: { dueDateMs: string }) => {
  const dueTime = Number(dueDateMs);

  return (
    <div className="flex items-start gap-1">
      <span className="text-[#84868C]">SLA:</span>
      <div className="text-[#F05B5B] flex flex-col items-end gap-1">
        <div>{dayjs(dueTime).format('YYYY-MM-DD HH:mm:ss')}</div>
        {dueTime > Date.now() && <CountDown time={dueTime} className={countdownCls} />}
      </div>
    </div>
  );
};

const DisplayStatus = ({
  displayStatusText,
  dueDateMs,
}: {
  displayStatusText: string | null;
  dueDateMs?: string;
}) => {
  if (displayStatusText) {
    return <div className="text-[#515359]">{displayStatusText}</div>;
  }

  if (dueDateMs) {
    return <DueTime dueDateMs={dueDateMs} />;
  }

  return null;
};

const LazadaOrderReturnItem = (props: LazadaOrderReturnItemProps) => {
  const { returnSn, returnItem, unit, multiple } = props;
  const { t } = useTranslate();
  const { getReasonLabel } = useGetReasonLabel();
  const statusLabel = useStatusLabel();

  const { visible, show, hide, meta } = useModal<StatusHistory[]>();
  const [openChatHistory, chatHistoryModal] = useChatHistory();

  const { checkReturnOrderItemIds, onCheckReturnOrderItemId } = useLazadaOrderReturnDetailContext();

  const {
    sellerSku,
    itemImage,
    returnOrderItemId,
    itemPrice,
    refundAmount,
    orderItemId,
    reason,
    operate,
    displayStatus,
    dueDateMs,
    returnStatusHistory,
  } = returnItem;

  const list = useMemo(
    () => [
      {
        title: t('ViewRefund.卖家SKU ID'),
        value: sellerSku,
      },
      {
        title: t('ViewRefund.已付价格+运费'),
        value: `${unit} ${formatNumber(itemPrice, '0,0.00')}`,
      },
      {
        title: t('ViewRefund.退货商品编号'),
        value: returnOrderItemId ?? '-',
      },
      {
        title: t('trade.退款金额'),
        value: `${unit} ${formatNumber(refundAmount, '0,0.00')}`,
      },
      {
        title: t('ViewRefund.订单商品编号'),
        value: orderItemId ?? '-',
      },
      {
        title: t('ViewRefund.退货原因'),
        value: getReasonLabel(reason || '-', 'LAZADA'),
      },
    ],
    [
      getReasonLabel,
      itemPrice,
      orderItemId,
      reason,
      refundAmount,
      returnOrderItemId,
      sellerSku,
      t,
      unit,
    ],
  );

  const showStatus =
    operate?.includes('STATUS_HISTORY') && !!displayStatus && !!statusLabel?.[displayStatus];

  const statusHistoryModal = meta && (
    <StatusHistoryModal visible={visible} onCancel={() => hide()} data={meta} />
  );

  const isChecked = useMemo(() => {
    return checkReturnOrderItemIds.includes(returnOrderItemId);
  }, [checkReturnOrderItemIds, returnOrderItemId]);

  return (
    <div className="flex items-start gap-2">
      <div className="flex items-center gap-2">
        {multiple && (
          <Checkbox
            checked={isChecked}
            onChange={() => {
              if (returnOrderItemId) {
                onCheckReturnOrderItemId(returnOrderItemId);
              }
            }}
          />
        )}
        <ProductInfoImage
          imgUrl={itemImage?.[0] ?? ''}
          itemName={returnItem.itemName}
          platform="LAZADA"
        />
      </div>
      <div className="flex-1 min-w-0">
        <ProductInfoTitle title={returnItem.itemName} />
        <div className="flex items-start gap-4">
          <div className="flex-1 grid grid-cols-2 gap-y-1 gap-x-4 mt-1">
            {list.map((item) => {
              if (!item.value) return null;
              return (
                <div key={item.title} className="flex items-center gap-1">
                  <span className="text-[#84868C]">{item.title}:</span>
                  <CopyText className="text-[#515359]" text={item.value} showIcon />
                </div>
              );
            })}
            {showStatus && (
              <div className="flex items-center gap-1">
                <span className="text-[#84868C]">{t('aide.状态')}:</span>
                <span
                  className="text-[#336FFF] cursor-pointer hover:opacity-80"
                  onClick={() => show(returnStatusHistory)}
                >
                  {statusLabel[displayStatus]}
                </span>
              </div>
            )}
            {!!returnOrderItemId && (
              <span
                className="text-[#336FFF] cursor-pointer hover:opacity-80"
                onClick={() => openChatHistory(returnSn, returnOrderItemId)}
              >
                {t('ViewRefund.通讯历史')}
              </span>
            )}
          </div>
          {!!displayStatus && (
            <div className="flex-shrink-0">
              <DisplayStatus
                displayStatusText={
                  displayStatus !== 'REQUEST_INITIATE' ? statusLabel[displayStatus] || '-' : null
                }
                dueDateMs={dueDateMs}
              />
            </div>
          )}
        </div>
      </div>
      {statusHistoryModal}
      {chatHistoryModal}
    </div>
  );
};

export const LazadaOrderReturnItems = (props: LazadaOrderReturnItemsProps) => {
  const { returnSn, returnItems, unit, lazadaReturnOps } = props;
  const { t } = useTranslate();
  const { onCheckReturnOrderItemId } = useLazadaOrderReturnDetailContext();

  const { showMultipleCheck } = lazadaReturnOps;

  useEffect(() => {
    if (!showMultipleCheck && returnItems?.[0]?.returnOrderItemId) {
      onCheckReturnOrderItemId(returnItems[0].returnOrderItemId);
    }
  }, [showMultipleCheck, onCheckReturnOrderItemId, returnItems]);

  return (
    <DetailCard title={t('x个商品', { count: returnItems.length })}>
      <div className="flex flex-col gap-4 bg-[#F7F9FA] rounded p-3">
        {returnItems.map((item) => (
          <LazadaOrderReturnItem
            key={item.returnOrderItemId}
            returnSn={returnSn}
            returnItem={item}
            unit={unit}
            multiple={showMultipleCheck}
          />
        ))}
      </div>
    </DetailCard>
  );
};

const countdownCls = css`
  color: #f05b5b !important;

  & > span {
    width: 22px !important;
    height: 20px !important;
    border-color: rgba(240, 91, 91, 0.66) !important;
  }
`;
